'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import EvtNaviItemType2 from '@wconcept/wconcept-ui/components/molecules/_item/EvtNaviItemType2';
import useGa4Event from '@hooks/useGa4Event';
import useHistoryRouter from '@hooks/useLinkTo';
import { useSearchParams } from 'next/navigation';
import type { ICommonNavigation } from '@apis/renewal/promotion';
import { requestGetCommonNavigation } from '@apis/renewal/promotion';
import dayjs from 'dayjs';

interface UseCommonNavigationModuleParams {
  navigationId: number;
  promotionId: number;
  promotionTitle: string;
}

export default function useCommonNavigationModule({
  navigationId,
  promotionId,
  promotionTitle,
}: UseCommonNavigationModuleParams) {
  const [naviData, setNaviData] = useState<ICommonNavigation | undefined>(
    undefined
  );
  const [previewDate, setPreviewDate] = useState<string | undefined>(undefined);
  useEffect(() => {
    setPreviewDate(
      new URLSearchParams(window.location.search).get('previewDate') ||
        undefined
    );
  }, []);
  const [activeSection, setActiveSection] = useState<number | undefined>(
    undefined
  );
  const { sendGa4TabClick } = useGa4Event();
  const { linkTo } = useHistoryRouter();

  const formatDisplayDate = (date: string) => {
    const [year, month, day] = dayjs(date).format('YYYY-MM-DD').split('-');
    return `${year}-${month}-${day} 00:00:00`;
  };

  const getNaviData = useCallback(async () => {
    const formattedPreviewDate = previewDate
      ? formatDisplayDate(previewDate)
      : undefined;
    const response = await requestGetCommonNavigation(
      navigationId,
      Number(promotionId),
      formattedPreviewDate,
      'MEGA_PROMOTION'
    );

    const findIndex = response?.tabs.findIndex(
      v =>
        v.landingType === 'MEGA_PROMOTION' &&
        v.landingId?.toString() === promotionId?.toString()
    );
    if (findIndex !== undefined && findIndex !== -1) {
      setActiveSection(findIndex);
    }
    setNaviData(response);
  }, [navigationId, promotionId, previewDate]);

  useEffect(() => {
    getNaviData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTabTitle = useCallback(
    (index: number) => {
      const result = naviData?.tabs?.[index]?.tabTitle1 || '';
      return result;
    },
    [naviData]
  );

  const getLandingUrl = useCallback(
    (
      landingType?: 'MEGA_PROMOTION' | 'BRAND_PROMOTION' | 'EVENT',
      landingId?: string
    ) => {
      if (landingType === 'MEGA_PROMOTION') {
        return `${process.env.NEXT_PUBLIC_WCONCEPT_DISPLAY_WEB}/rn/promotion/${landingId}`;
      }
      if (landingType === 'BRAND_PROMOTION') {
        return `${process.env.NEXT_PUBLIC_WCONCEPT_DISPLAY_WEB}/preshow`;
      }
      if (landingType === 'EVENT') {
        return `${process.env.NEXT_PUBLIC_EVENT_FRONT_URL}/event/${landingId}`;
      }
      return '';
    },
    []
  );

  const handleClick = useCallback(
    async (index: number) => {
      sendGa4TabClick({
        text: getTabTitle(index),
        area: 'promotion_commonnavigation_tab',
        listNo: index + 1,
        page: 'promotion',
      });
      const landingUrl = getLandingUrl(
        naviData?.tabs?.[index]?.landingType,
        naviData?.tabs?.[index]?.landingId
      );
      try {
        const url = new URL(landingUrl);

        if (landingUrl && url.pathname !== window.location.pathname) {
          linkTo.push({
            url: landingUrl,
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    [sendGa4TabClick, getTabTitle, getLandingUrl, naviData?.tabs, linkTo]
  );

  const items = useMemo(
    () =>
      naviData?.tabs?.map((v, i) => (
        <EvtNaviItemType2
          key={v.tabId}
          active={i === activeSection}
          title={v.tabTitle1}
          $offBgColor={naviData?.offBgColor}
          $onBgColor={naviData?.onBgColor}
          $offTextColor={naviData?.offTextColor}
          $onTextColor={naviData?.onTextColor}
          onClick={() => {
            handleClick(i);
          }}
        />
      )),
    [naviData, activeSection, handleClick]
  );

  return {
    items,
    activeSection,
  } as const;
}
